import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import type { Address } from 'viem'
import type { EulerIndexerProduct } from '@/domain/euler/elends/types'
import { useELendsList } from '@/domain/euler/elends/useELendInfo'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { type VaultMarket, getVaultMarket } from '@/features/evault-details/data/products'
import { isPrimeVault, isYieldVault, type MarketResolver } from '../constants'

interface VaultClassifierValue {
  isPrime: (address: string) => boolean
  isYield: (address: string) => boolean
  resolveMarket: MarketResolver
}

const VaultClassifierContext = createContext<VaultClassifierValue | null>(null)

export function VaultClassifierProvider({ children }: { children: ReactNode }) {
  const { vaults } = useELendsList(E_LEND_CHAIN_ID)

  const productsByVault = useMemo(() => {
    const map = new Map<string, EulerIndexerProduct[]>()
    for (const vault of vaults) {
      if (vault.products.length > 0) {
        map.set(vault.address.toLowerCase(), vault.products)
      }
    }
    return map
  }, [vaults])

  const resolveMarket = useCallback(
    (vaultAddress: Address): VaultMarket | undefined => {
      const products = productsByVault.get(vaultAddress.toLowerCase())
      return getVaultMarket(vaultAddress, products)
    },
    [productsByVault],
  )

  const isPrime = useCallback(
    (address: string) => isPrimeVault(address, resolveMarket),
    [resolveMarket],
  )

  const isYield = useCallback(
    (address: string) => isYieldVault(address, resolveMarket),
    [resolveMarket],
  )

  const value = useMemo(() => ({ isPrime, isYield, resolveMarket }), [isPrime, isYield, resolveMarket])

  return <VaultClassifierContext.Provider value={value}>{children}</VaultClassifierContext.Provider>
}

export function useVaultClassifier(): VaultClassifierValue {
  const ctx = useContext(VaultClassifierContext)
  if (!ctx) {
    throw new Error('useVaultClassifier must be used within a VaultClassifierProvider')
  }
  return ctx
}
